<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { OrderPaymentsVariant, OrderStatus } from "@/constants/order";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import OrderFile from "@/views/orders/edit/components/OrderFile.vue";
import OrderPayments from "@/components/order/OrderPayments.vue";
import { getOrderPaymentInvoiceIds, isInvoiceDeletedByStatusChange } from "@/utils/orderPayments";

import type { OrderPayment } from "@/api/orders/dto/order.dto";
import OrderStatusSelect from "@/components/selects/OrderStatusSelect.vue";

const { t } = useI18n();

const status = defineModel<OrderStatus>("status", { default: OrderStatus.inProgress });
const payments = defineModel<OrderPayment[]>("payments", { default: () => [] });
const file = defineModel<File | null>("file", { default: null });

const props = defineProps<{
  orderId: string;
  orderNumber?: number;
  isStatusDisabled: boolean;
  statusFromApi?: OrderStatus;
  isFileVisible: boolean;
  fileLabel: string;
  isReturnedFromDryRun: boolean;
}>();

const isCompleted = computed<boolean>(() => props.statusFromApi === OrderStatus.completed);

const deletedInvoiceIds = computed<string[]>(() => {
  if (!props.isReturnedFromDryRun && !isInvoiceDeletedByStatusChange(props.statusFromApi, status.value)) {
    return [];
  }

  return getOrderPaymentInvoiceIds(payments.value);
});
</script>

<template>
  <div class="d-flex flex-column row-gap-6 pa-5 rounded-lg order-section">
    <div class="d-flex flex-column row-gap-4">
      <div class="d-flex align-center col-gap-1 order-section__title">
        <router-link
          :to="{ name: 'order-view', params: { id: props.orderId } }"
          class="text-decoration-none text-primary"
          target="_blank"
        >
          {{ t("orders.order-number", { number: props.orderNumber }) }}
        </router-link>
        <span>{{ t("trip.order-details") }}</span>
      </div>

      <order-status-select
        v-model="status"
        :disabled="props.isStatusDisabled || isCompleted"
        :status-from-api="props.statusFromApi"
      />

      <alert-message
        v-if="props.isStatusDisabled || isCompleted"
        bg-color="indigo-lighten-5"
        border-color="none"
        class="line-height-16px"
        icon-color="indigo"
      >
        <span class="text-indigo">Order status can’t be changed while it has active travel orders</span>
      </alert-message>

      <v-alert
        v-for="invoiceId in deletedInvoiceIds"
        :key="invoiceId"
        data-qa="order-invoice-deleted-alert"
        density="compact"
        icon="mdi-alert-outline"
        type="warning"
        variant="outlined"
      >
        {{ t("trip.invoice-will-be-deleted", { invoice: invoiceId }) }}
      </v-alert>
    </div>

    <order-payments v-model="payments" :order-status="status" :variant="OrderPaymentsVariant.Cards" can-restore>
      <template v-if="props.isFileVisible" #mandatory-payment>
        <order-file v-model="file" :label="props.fileLabel" />
      </template>
    </order-payments>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-section {
  border: 1px solid $grey-lighter-contrast;

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $grey-navy;
  }
}
</style>
